import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/joy';
import { FaCalendarAlt, FaCheck, FaEllipsisV, FaPlus } from 'react-icons/fa';
import { IconWrapper } from '../../components/IconWrapper';
import type { ProjectEvent } from '../../components/EventManager';
import type { Project } from '../../types/project';
import { directSupabase } from '../../lib/supabaseClient';
import { getGraphClientSilent, outlookCalendarItemUrl } from '../../utils/graphService';
import { WORK_CATEGORIES } from '../../services/calendarService';
import { taskNameForLinkedEvent } from './types';
import { setKeyDateManualComplete } from './keyDateCompletion';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';
import { listProjectJobCards } from './jobCard.service';
import type { JobCardRecord } from './jobCard.types';
import { formatInstallSpan, installDateOverrideFrom, jobCardInstallSpan } from './jobCardInstallDate';

type CalendarProjectEvent = ProjectEvent & { metadata?: { category?: string; manualComplete?: boolean } };

const CALENDAR_EVENT_TYPES = [
  'firstSurveyAppointmentConfirmation',
  'finalSurveyAppointmentConfirmation',
  'installationDateConfirmation',
  'deliveryDateConfirmation',
] as const;

type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];

function parseEventDate(dateStr: string) {
  if (dateStr && !dateStr.match(/(Z|[+-][0-9]{2}:?[0-9]{2})$/)) {
    return new Date(`${dateStr}Z`);
  }
  return new Date(dateStr);
}

function latestOf(events: CalendarProjectEvent[], type: CalendarEventType) {
  return events
    .filter((event) => event.event_type === type && event.status !== 'cancelled')
    .sort((a, b) => {
      const aDated = a.event_date ? 1 : 0;
      const bDated = b.event_date ? 1 : 0;
      if (aDated !== bDated) return bDated - aDated;
      const aMs = Date.parse(a.event_date || a.created_at || '') || 0;
      const bMs = Date.parse(b.event_date || b.created_at || '') || 0;
      return bMs - aMs;
    })[0];
}

function isManualComplete(event: CalendarProjectEvent | undefined) {
  if (!event || event.event_date) return false;
  if (event.status === 'completed') return true;
  return !!event.metadata?.manualComplete;
}

function workCategoryFor(name: string | null) {
  if (!name) return undefined;
  const needle = name.trim().toLowerCase();
  return WORK_CATEGORIES.find((category) => (
    category.name.toLowerCase() === needle || category.id === needle
  ));
}

function categoryAssigneeLabel(event: CalendarProjectEvent | undefined, outlookCategory?: string): string | null {
  const fromOutlook = String(outlookCategory || '').trim();
  if (fromOutlook) return fromOutlook;
  const meta = event?.metadata?.category;
  if (!meta) return null;
  return workCategoryFor(String(meta))?.name || String(meta);
}

function categoryChipSx(name: string | null) {
  const color = workCategoryFor(name)?.color;
  if (!color) return undefined;
  const hex = color.replace('#', '');
  if (hex.length < 6) return { bgcolor: color, color: '#fff' };
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const light = (r * 299 + g * 587 + b * 114) / 1000 > 160;
  return { bgcolor: color, color: light ? '#111' : '#fff' };
}

async function fetchOutlookCategories(calendarEventIds: string[]): Promise<Record<string, string>> {
  const unique = Array.from(new Set(calendarEventIds.map((id) => String(id || '').trim()).filter(Boolean)));
  if (unique.length === 0) return {};
  const client = await getGraphClientSilent();
  if (!client) return {};
  const calendarsResponse = await client.api('/me/calendars').select('id,name,isDefaultCalendar').get();
  const calendars = calendarsResponse?.value || [];
  const calendar = calendars.find((entry: { isDefaultCalendar?: boolean }) => entry.isDefaultCalendar)
    || calendars.find((entry: { name?: string }) => entry.name === 'Calendar')
    || calendars[0];
  const calendarId = calendar?.id;
  const categories: Record<string, string> = {};

  if (calendarId) {
    const batchRequests = unique.map((id, index) => ({
      id: String(index + 1),
      method: 'GET',
      url: `/me/calendars/${calendarId}/events/${id}?$select=id,categories`,
    }));
    const batchResp = await client.api('/$batch').post({ requests: batchRequests });
    (batchResp?.responses || []).forEach((response: { id?: string; status?: number; body?: { id?: string; categories?: string[] } }) => {
      if (response.status !== 200 || !response.body) return;
      const requestIndex = Number(response.id) - 1;
      const eventId = unique[requestIndex] || response.body.id;
      if (!eventId) return;
      const name = Array.isArray(response.body.categories) && response.body.categories[0]
        ? String(response.body.categories[0])
        : '';
      categories[eventId] = name;
    });
  }

  const missing = unique.filter((id) => !(id in categories));
  if (missing.length > 0) {
    await Promise.all(missing.map(async (id) => {
      try {
        const event = await client.api(`/me/events/${encodeURIComponent(id)}`).select('categories').get();
        categories[id] = Array.isArray(event?.categories) && event.categories[0] ? String(event.categories[0]) : '';
      } catch {
        categories[id] = '';
      }
    }));
  }

  return categories;
}

const DATE_TILE_SX = {
  width: 76,
  height: 80,
  flexShrink: 0,
  borderRadius: '8px',
} as const;

function TbcMark({ onClick, label }: { onClick?: () => void; label?: string }) {
  return (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={label || 'Mark complete'}
      sx={{
        ...DATE_TILE_SX,
        border: 'none',
        bgcolor: 'primary.500',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        letterSpacing: 0.6,
        fontSize: '0.85rem',
        p: 0,
        ...(onClick ? {
          cursor: 'pointer',
          transition: 'filter 0.15s ease',
          '&:hover': { filter: 'brightness(1.08)' },
        } : {}),
      }}
    >
      TBC
    </Box>
  );
}

function CompleteMark({ onClick, label }: { onClick?: () => void; label?: string }) {
  return (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={label || 'Undo complete'}
      sx={{
        ...DATE_TILE_SX,
        border: '1px solid',
        borderColor: 'success.outlinedBorder',
        boxShadow: 'sm',
        bgcolor: 'success.500',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
        font: 'inherit',
        ...(onClick ? {
          cursor: 'pointer',
          transition: 'filter 0.15s ease',
          '&:hover': { filter: 'brightness(1.06)' },
        } : {}),
      }}
    >
      <IconWrapper icon={FaCheck} style={{ width: 28, height: 28 }} />
    </Box>
  );
}

function CalendarDateMark({
  date,
  href,
  label,
}: {
  date: Date | null;
  href?: string;
  label?: string;
}) {
  const clickable = Boolean(href);
  const empty = !date || Number.isNaN(date.getTime());
  if (empty) return <TbcMark />;
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const monthIndex = Number(date.toLocaleDateString('en-GB', { month: 'numeric', timeZone: 'Europe/London' })) - 1;
  const month = monthNames[monthIndex] || 'DATE';
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', timeZone: 'Europe/London' });
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'Europe/London' });

  const mark = (
    <>
      <Box
        sx={{
          bgcolor: 'primary.500',
          color: '#fff',
          textAlign: 'center',
          py: 0.4,
        }}
      >
        <Typography level="body-xs" sx={{ fontWeight: 800, letterSpacing: 1.2, color: 'inherit', lineHeight: 1.2 }}>
          {month}
        </Typography>
      </Box>
      <Box
        sx={{
          minHeight: 52,
          px: 0.5,
          py: 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
        }}
      >
        <Typography
          level="h2"
          sx={{
            m: 0,
            lineHeight: 1.05,
            fontWeight: 800,
            fontSize: '1.75rem',
          }}
        >
          {day}
        </Typography>
        {weekday ? (
          <Typography level="body-xs" sx={{ color: 'neutral.500', lineHeight: 1.2 }}>
            {weekday}
          </Typography>
        ) : null}
      </Box>
    </>
  );

  const rootSx = {
    ...DATE_TILE_SX,
    display: 'block',
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'primary.outlinedBorder',
    boxShadow: 'sm',
    bgcolor: 'background.surface',
    color: 'text.primary',
    textDecoration: 'none',
    font: 'inherit',
    lineHeight: 'normal',
    ...(clickable ? {
      cursor: 'pointer',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: 'md', textDecoration: 'none', color: 'text.primary' },
    } : {}),
  } as const;

  if (!href) {
    return <Box sx={rootSx}>{mark}</Box>;
  }

  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label || 'Open in Outlook'}
      sx={rootSx}
    >
      {mark}
    </Box>
  );
}

interface WorkspaceCalendarEventsCardProps {
  project: Project;
  refreshKey?: number;
  onAddEvent?: () => void;
  onSchedule?: (eventType: CalendarEventType) => void;
  onLinkedTaskChange?: (taskName: string, completed: boolean) => Promise<void>;
}

function formatTimeRange(date: Date) {
  const start = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
  if (start === '00:00') return '';
  const end = new Date(date.getTime() + 60 * 60 * 1000);
  const endLabel = end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
  return `${start} – ${endLabel}`;
}

const WorkspaceCalendarEventsCard: React.FC<WorkspaceCalendarEventsCardProps> = ({
  project,
  refreshKey = 0,
  onAddEvent,
  onSchedule,
  onLinkedTaskChange,
}) => {
  const [events, setEvents] = useState<CalendarProjectEvent[]>([]);
  const [categoryByEventId, setCategoryByEventId] = useState<Record<string, string>>({});
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [savingType, setSavingType] = useState<CalendarEventType | null>(null);

  const syncLinkedTasks = (rows: CalendarProjectEvent[]) => {
    CALENDAR_EVENT_TYPES.forEach((type) => {
      const taskName = taskNameForLinkedEvent(type);
      if (!taskName) return;
      const event = latestOf(rows, type);
      // Only a key date marked done by hand ticks its step here. A booked installation or
      // delivery date ticks "Schedule ..." from the booking itself (checklistRecords), so
      // opening this card no longer changes the checklist.
      if (isManualComplete(event)) {
        void onLinkedTaskChange?.(taskName, true);
      }
    });
  };

  const loadEvents = async () => {
    setCategoriesReady(false);
    const { data } = await directSupabase
      .from('project_events')
      .select('*')
      .eq('project_id', project.id)
      .in('event_type', [...CALENDAR_EVENT_TYPES])
      .order('event_date', { ascending: false });
    const rows = (data || []) as CalendarProjectEvent[];
    setEvents(rows);
    syncLinkedTasks(rows);

    const calendarIds = rows
      .map((row) => row.calendar_event_id)
      .filter((id): id is string => Boolean(id));
    if (calendarIds.length === 0) {
      setCategoryByEventId({});
      setCategoriesReady(true);
      return;
    }
    try {
      const categories = await fetchOutlookCategories(calendarIds);
      setCategoryByEventId(categories);
    } catch {
      setCategoryByEventId({});
    }
    setCategoriesReady(true);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setCategoriesReady(false);
      const { data } = await directSupabase
        .from('project_events')
        .select('*')
        .eq('project_id', project.id)
        .in('event_type', [...CALENDAR_EVENT_TYPES])
        .order('event_date', { ascending: false });
      const rows = (data || []) as CalendarProjectEvent[];
      if (cancelled) return;
      setEvents(rows);
      // Job cards after the first are return visits / crew splits, listed under the installation
      listProjectJobCards(project.id)
        .then((cards) => { if (!cancelled) setLaterCards(cards.slice(1)); })
        .catch(() => { if (!cancelled) setLaterCards([]); });
      syncLinkedTasks(rows);

      const calendarIds = rows
        .map((row) => row.calendar_event_id)
        .filter((id): id is string => Boolean(id));
      if (calendarIds.length === 0) {
        setCategoryByEventId({});
        setCategoriesReady(true);
        return;
      }
      try {
        const categories = await fetchOutlookCategories(calendarIds);
        if (cancelled) return;
        setCategoryByEventId(categories);
      } catch {
        if (cancelled) return;
        setCategoryByEventId({});
      }
      if (!cancelled) setCategoriesReady(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [project.id, refreshKey]);

  const markComplete = async (eventType: CalendarEventType) => {
    if (savingType) return;
    setSavingType(eventType);
    try {
      await setKeyDateManualComplete(project.id, eventType, true);
      const taskName = taskNameForLinkedEvent(eventType);
      if (taskName) await onLinkedTaskChange?.(taskName, true);
      await loadEvents();
    } finally {
      setSavingType(null);
    }
  };

  const undoComplete = async (eventType: CalendarEventType) => {
    if (savingType) return;
    setSavingType(eventType);
    try {
      await setKeyDateManualComplete(project.id, eventType, false);
      const taskName = taskNameForLinkedEvent(eventType);
      if (taskName) await onLinkedTaskChange?.(taskName, false);
      await loadEvents();
    } finally {
      setSavingType(null);
    }
  };

  const [laterCards, setLaterCards] = useState<JobCardRecord[]>([]);
  const jobCompleted = workspaceLifecycleStatus(project) === 'Completed';

  const slots: Array<{ key: CalendarEventType; label: string }> = [
    { key: 'firstSurveyAppointmentConfirmation', label: 'First survey' },
    { key: 'finalSurveyAppointmentConfirmation', label: 'Final survey' },
    project.supply_only
      ? { key: 'deliveryDateConfirmation', label: 'Delivery Scheduled' }
      : { key: 'installationDateConfirmation', label: 'Installation Scheduled' },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={FaCalendarAlt}
          title="Key dates"
          description={`First survey, final survey, and ${project.supply_only ? 'delivery' : 'installation'} dates booked for this job.`}
          action={onAddEvent ? (
            <Button size="sm" variant="plain" startDecorator={<IconWrapper icon={FaPlus} />} onClick={onAddEvent}>
              Add event
            </Button>
          ) : undefined}
        />
        <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
          {slots.map((slot) => {
            const event = latestOf(events, slot.key);
            const date = event?.event_date ? parseEventDate(event.event_date) : null;
            const scheduled = !!(date && !Number.isNaN(date.getTime()));
            const surveySlot = slot.key === 'firstSurveyAppointmentConfirmation'
              || slot.key === 'finalSurveyAppointmentConfirmation';
            // Installation/Delivery completes with the job, never by a manual tick.
            const completed = !scheduled && (surveySlot ? isManualComplete(event) : jobCompleted);
            const timeRange = scheduled ? formatTimeRange(date) : '';
            const outlookHref = event?.calendar_event_id ? outlookCalendarItemUrl(event.calendar_event_id) : undefined;
            const assignee = categoryAssigneeLabel(
              event,
              event?.calendar_event_id ? categoryByEventId[event.calendar_event_id] : undefined,
            );
            return (
              <Box
                key={slot.key}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  py: 1.25,
                }}
              >
                {scheduled ? (
                  <CalendarDateMark
                    date={date}
                    href={outlookHref}
                    label={outlookHref ? `Open ${slot.label} in Outlook` : undefined}
                  />
                ) : completed ? (
                  <CompleteMark
                    onClick={surveySlot ? () => { void undoComplete(slot.key); } : undefined}
                    label={surveySlot ? `Undo complete for ${slot.label}` : `${slot.label} complete`}
                  />
                ) : (
                  <TbcMark
                    onClick={surveySlot ? () => { void markComplete(slot.key); } : undefined}
                    label={surveySlot ? `Mark ${slot.label} complete` : `${slot.label} not scheduled`}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                  <Typography level="body-sm" fontWeight="lg">{slot.label}</Typography>
                  {scheduled ? (
                    <Stack sx={{ mt: 0.35 }} gap={0.25}>
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        {timeRange && (
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>{timeRange}</Typography>
                        )}
                        {categoriesReady && assignee && (
                          <Chip size="sm" variant="soft" sx={categoryChipSx(assignee)}>
                            {assignee}
                          </Chip>
                        )}
                      </Stack>
                      {!surveySlot && jobCompleted ? (
                          <Typography level="body-xs" sx={{ color: 'success.600' }}>
                            Complete
                          </Typography>
                        ) : (
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                            Scheduled
                          </Typography>
                        )}
                    </Stack>
                  ) : completed ? (
                    <Typography level="body-xs" sx={{ color: 'success.600', mt: 0.35 }}>
                      Complete
                    </Typography>
                  ) : (
                    <Stack direction="row" gap={0.75} alignItems="center" sx={{ mt: 0.35 }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'neutral.300',
                          flexShrink: 0,
                        }}
                      />
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        Not scheduled
                      </Typography>
                    </Stack>
                  )}
                </Box>
                {!scheduled && !completed && (
                  <Stack direction="row" gap={0.75} flexShrink={0} sx={{ ml: 'auto' }}>
                    {onSchedule && (
                      <Button size="sm" variant="outlined" onClick={() => onSchedule(slot.key)}>
                        Schedule
                      </Button>
                    )}
                    {surveySlot && (
                      <Button
                        size="sm"
                        variant="plain"
                        color="success"
                        loading={savingType === slot.key}
                        onClick={() => { void markComplete(slot.key); }}
                      >
                        Complete
                      </Button>
                    )}
                  </Stack>
                )}
                {outlookHref && (
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{ root: { size: 'sm', variant: 'plain', 'aria-label': `${slot.label} actions` } }}
                    >
                      <IconWrapper icon={FaEllipsisV} />
                    </MenuButton>
                    <Menu placement="bottom-end">
                      <MenuItem
                        component="a"
                        href={outlookHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Outlook
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                )}
                {completed && surveySlot && (
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{ root: { size: 'sm', variant: 'plain', 'aria-label': `${slot.label} actions` } }}
                    >
                      <IconWrapper icon={FaEllipsisV} />
                    </MenuButton>
                    <Menu placement="bottom-end">
                      <MenuItem onClick={() => { void undoComplete(slot.key); }}>
                        Undo complete
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                )}
              </Box>
            );
          })}
          {laterCards.map((card) => {
            const booking = latestOf(events, project.supply_only ? 'deliveryDateConfirmation' : 'installationDateConfirmation');
            const span = jobCardInstallSpan({
              calendar: booking || null,
              overrideStart: installDateOverrideFrom(card, project),
              overrideEnd: card.install_end_date_override,
              followsBooking: card.follows_booking,
            });
            const label = formatInstallSpan(span);
            const cardHref = card.calendar_event_id ? outlookCalendarItemUrl(card.calendar_event_id) : undefined;
            return (
              <Box key={card.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1, pl: 2 }}>
                <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                  <Typography level="body-sm" fontWeight="lg">{card.title}</Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                    {card.install_date_override
                      ? label || 'Not scheduled'
                      : `Same days as the ${project.supply_only ? 'delivery' : 'installation'}`}
                  </Typography>
                </Box>
                {cardHref && (
                  <Button size="sm" variant="plain" component="a" href={cardHref} target="_blank" rel="noopener noreferrer">
                    Outlook
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WorkspaceCalendarEventsCard;
export { CALENDAR_EVENT_TYPES };
